import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type MapPrivacyLevel } from '~/_shared/types/map';
import { openUrlInNewTab } from '~/_shared/utils/link/link';
import { useRemoveMemberFromMap } from '~/clientTeamManagement/teamShareMapModal/useRemoveMemberFromMap.hook';
import { type MapListingItem } from '~/map/listing/item/mapListingItem';
import { checkMapCanBeShared } from '~/map/listing/mapListing.helpers';
import type { MapListingRowItem } from '~/map/listing/tableData/mapRow/mapRow.component';
import { MapRowActionsComponent } from '~/map/listing/tableData/mapRow/mapRowActions.component';
import {
  getMapUrl, getRelativeMapPresentationalUrl,
} from '~/map/map.helpers';
import { useModal } from '~/modal/useModal.hook';
import { SidebarApp } from '~/sidebar/sidebarApp.enum';
import { OPENED_APP_FRAGMENT_IDENTIFIER } from '~/store/frontendState/leftSidebar/leftSidebar.constants';
import { mapListingChanged } from '~/store/frontendState/mapListing/mapListing.actionCreators';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import { type ChangePrivacyLevelProps } from '../../useMapListingItems';

type MapRowActionsProps = Readonly<{
  isOwner: boolean;
  mapListingItem: MapListingRowItem;
  parentMapItem?: MapListingItem;
  width: number;
  onShareSuccess: (props: ChangePrivacyLevelProps) => void;
}>;

export const MapRowActionsContainer: FC<MapRowActionsProps> = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useUserIdSelector();
  const { removeUserFromMapRequest } = useRemoveMemberFromMap();

  const { openModal: openCopyMapModal } = useModal('CopyMap');
  const { openModal: openShareSettingsModal } = useModal('ShareMap');
  const { openModal: openTeamShareMapModal } = useModal('TeamShareMap');

  const isBaseLayer = !!props.mapListingItem.isBaseLayer;
  const isSnapshot = !!props.parentMapItem && !isBaseLayer;
  const isReadOnlyMap = !!props.mapListingItem.isReadOnly;
  const isShareActive = checkMapCanBeShared(props.mapListingItem);

  const handleShareSuccess = useCallback((newPrivacyLevel: MapPrivacyLevel) => {
    const onShareSuccess = props.onShareSuccess;
    onShareSuccess({
      isBaseLayer,
      mapId: props.mapListingItem.id,
      newPrivacyLevel,
      parentMapId: props.parentMapItem?.id,
    });
  }, [isBaseLayer, props.mapListingItem.id, props.onShareSuccess, props.parentMapItem?.id]);

  const onMapShareSettingsClick = useCallback(() =>
    openShareSettingsModal({
      initialShareId: props.mapListingItem.shareId ?? undefined,
      mapId: props.mapListingItem.id,
      mapName: props.mapListingItem.name,
      mapPrivacyLevel: props.mapListingItem.privacy,
      isSnapshot,
      parentMapPrivacy: props.parentMapItem?.privacy || null,
      onSuccess: handleShareSuccess,
      mapSource: props.mapListingItem.source,
    }), [openShareSettingsModal, props.mapListingItem.id, props.mapListingItem.name, props.parentMapItem?.privacy, isSnapshot, handleShareSuccess, props.mapListingItem.source, props.mapListingItem.privacy, props.mapListingItem.shareId]);

  const onTeamShareMapClick = useCallback(() =>
    openTeamShareMapModal({
      mapId: props.mapListingItem.id,
      mapName: props.mapListingItem.name,
      isSnapshot,
    }), [isSnapshot, openTeamShareMapModal, props.mapListingItem.id, props.mapListingItem.name]);

  const onMapCopyClick = useCallback(() =>
    openCopyMapModal({
      mapId: props.mapListingItem.id,
      onMapCopySuccess: () => dispatch(mapListingChanged()),
      originalMapName: props.mapListingItem.name,
    }), [dispatch, openCopyMapModal, props.mapListingItem.id, props.mapListingItem.name]);

  const onSettingsClick = useCallback(() =>
    navigate(getMapUrl(props.mapListingItem.id, undefined, { [OPENED_APP_FRAGMENT_IDENTIFIER]: SidebarApp.MapSettings })),
  [navigate, props.mapListingItem.id]);

  const onOpenInShareModeClick = useCallback(() => {
    const shareId = props.mapListingItem.shareId;
    if (shareId) {
      openUrlInNewTab(getRelativeMapPresentationalUrl(shareId));
    }
  }, [props.mapListingItem.shareId]);

  const onRemoveMyselfFromMapClick = useCallback(() => {
    const entityAccessId = props.mapListingItem.sharedUsers.find(u => u.id === userId)?.accessId;
    if (!entityAccessId) {
      return;
    }

    removeUserFromMapRequest({
      entityAccessId,
      isCurrentUserSharee: true,
      onSuccess: () => {
        dispatch(mapListingChanged());
      },
    });
  }, [dispatch, props.mapListingItem.sharedUsers, removeUserFromMapRequest, userId]);

  return (
    <MapRowActionsComponent
      isSnapshot={isSnapshot}
      isOwner={props.isOwner}
      onSettingsClick={isReadOnlyMap ? undefined : onSettingsClick}
      onMapCopyClick={isShareActive ? onMapCopyClick : undefined}
      onTeamShareMapClick={onTeamShareMapClick}
      onMapShareSettingsClick={props.mapListingItem.shareId ? onMapShareSettingsClick : undefined}
      onOpenInShareModeClick={onOpenInShareModeClick}
      isShareActive={isShareActive}
      onRemoveMyselfFromMapClick={props.isOwner ? undefined : onRemoveMyselfFromMapClick}
      data-testid={`map-listing-control-menu-trigger-${props.mapListingItem.name}`}
    />
  );
};

MapRowActionsContainer.displayName = 'MapRowActionsContainer';
